import { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, Button, TextField, Grid, FormControl, InputLabel, Select, MenuItem, Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getLocations } from '../../../services/LocationService';
import B2BClientService from '../../../services/B2BClientService';

const AddB2BClientModal = ({ open, onClose, onSave }) => {
  const { t } = useTranslation();

  const [client, setClient] = useState({
    razonSocial: '',
    nombreFantasia: '',
    rut: '',
    direccionFiscal: '',
    contacto: '',
    email: '',
    telefono: '',
    creditDays: '',
    creditLimit: '',
    generalDiscount: '',
    commercialProfileId: '',
    clientType: 'EXTERNAL',
    defaultDestinationLocationId: '',
    notes: '',
    priceExceptions: []
  });
  const [locations, setLocations] = useState([]);
  const [commercialProfiles, setCommercialProfiles] = useState([]);
  const [profilesLoading, setProfilesLoading] = useState(true);
  const [profilesLoadFailed, setProfilesLoadFailed] = useState(false);

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!open) {
      setCommercialProfiles([]);
      setProfilesLoading(true);
      setProfilesLoadFailed(false);
      return;
    }

    let isCurrent = true;

    const loadInitialData = async () => {
      setProfilesLoading(true);
      setProfilesLoadFailed(false);

      try {
        const [locationsData, profilesData] = await Promise.all([
          getLocations(),
          B2BClientService.getCommercialProfiles()
        ]);
        if (!isCurrent) return;

        setLocations((locationsData || []).filter(location => !location.isPlant));
        setCommercialProfiles(profilesData || []);
      } catch (error) {
        if (!isCurrent) return;

        setLocations([]);
        setCommercialProfiles([]);
        setProfilesLoadFailed(true);
      } finally {
        if (isCurrent) {
          setProfilesLoading(false);
        }
      }
    };

    loadInitialData();

    return () => {
      isCurrent = false;
    };
  }, [open]);

  const profilePrerequisiteMissing =
    !profilesLoading && !profilesLoadFailed && commercialProfiles.length === 0;

  const handleFieldChange = (field, value) => {
    setClient(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const validateFields = () => {
    const newErrors = {};

    if (!client.razonSocial) {
      newErrors.razonSocial = t('business_name_required');
    }

    if (!client.nombreFantasia) {
      newErrors.nombreFantasia = t('fantasy_name_required');
    }

    if (!client.rut) {
      newErrors.rut = t('rut_required');
    }

    if (!client.direccionFiscal) {
      newErrors.direccionFiscal = t('address_required');
    }

    if (!client.contacto) {
      newErrors.contacto = t('contact_name_required');
    }

    if (!client.email) {
      newErrors.email = t('email_required');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(client.email)) {
      newErrors.email = t('invalid_email');
    }

    if (!client.telefono) {
      newErrors.telefono = t('phone_required');
    }

    if (client.clientType === 'FRANCHISE' && !client.defaultDestinationLocationId) {
      newErrors.defaultDestinationLocationId = 'Seleccioná la franquicia asignada';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (validateFields()) {
      // Preparar los datos según la estructura del backend
      const clientData = {
        rut: client.rut,
        razonSocial: client.razonSocial,
        nombreFantasia: client.nombreFantasia,
        direccionFiscal: client.direccionFiscal,
        telefono: client.telefono,
        email: client.email,
        contacto: client.contacto,
        creditDays: client.creditDays || 30, // Valor por defecto si está vacío
        creditLimit: client.creditLimit || 0.0, // Valor por defecto si está vacío
        generalDiscount: 0.0,
        commercialProfileId: client.commercialProfileId !== '' ? Number(client.commercialProfileId) : null,
        clientType: client.clientType,
        defaultDestinationLocationId:
          client.clientType === 'FRANCHISE' && client.defaultDestinationLocationId !== ''
            ? Number(client.defaultDestinationLocationId)
            : null,
        notes: client.notes,
        priceExceptions: []
      };
      
      onSave(clientData);
      setClient({
        razonSocial: '',
        nombreFantasia: '',
        rut: '',
        direccionFiscal: '',
        contacto: '',
        email: '',
        telefono: '',
        creditDays: '',
        creditLimit: '',
        generalDiscount: '',
        commercialProfileId: '',
        clientType: 'EXTERNAL',
        defaultDestinationLocationId: '',
        notes: '',
        priceExceptions: []
      });
      onClose();
    }
  };

  const handleClose = () => {
    setClient({
      razonSocial: '',
      nombreFantasia: '',
      rut: '',
      direccionFiscal: '',
      contacto: '',
      email: '',
        telefono: '',
        creditDays: '',
        creditLimit: '',
        generalDiscount: '',
        commercialProfileId: '',
        clientType: 'EXTERNAL',
        defaultDestinationLocationId: '',
        notes: '',
        priceExceptions: []
      });
    setErrors({});
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>{t('add_b2b_client')}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {profilePrerequisiteMissing && (
            <Grid item xs={12}>
              <Alert severity="error">
                Antes de crear un cliente B2B es necesario crear un perfil comercial.
              </Alert>
            </Grid>
          )}

          {/* Datos Básicos */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
              {t('basic_data')}
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <TextField
              label={t('business_name')}
              fullWidth
              value={client.razonSocial}
              onChange={e => handleFieldChange('razonSocial', e.target.value)}
              error={!!errors.razonSocial}
              helperText={errors.razonSocial}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label={t('fantasy_name')}
              fullWidth
              value={client.nombreFantasia}
              onChange={e => handleFieldChange('nombreFantasia', e.target.value)}
              error={!!errors.nombreFantasia}
              helperText={errors.nombreFantasia}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label={t('rut')}
              fullWidth
              value={client.rut}
              onChange={e => handleFieldChange('rut', e.target.value)}
              error={!!errors.rut}
              helperText={errors.rut}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label={t('address')}
              fullWidth
              value={client.direccionFiscal}
              onChange={e => handleFieldChange('direccionFiscal', e.target.value)}
              error={!!errors.direccionFiscal}
              helperText={errors.direccionFiscal}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label={t('phone')}
              fullWidth
              value={client.telefono}
              onChange={e => handleFieldChange('telefono', e.target.value)}
              error={!!errors.telefono}
              helperText={errors.telefono}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label={t('contact_name')}
              fullWidth
              value={client.contacto}
              onChange={e => handleFieldChange('contacto', e.target.value)}
              error={!!errors.contacto}
              helperText={errors.contacto}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label={t('email')}
              fullWidth
              type="email"
              value={client.email}
              onChange={e => handleFieldChange('email', e.target.value)}
              error={!!errors.email}
              helperText={errors.email}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Tipo de cliente</InputLabel>
              <Select
                value={client.clientType}
                label="Tipo de cliente"
                onChange={e => handleFieldChange('clientType', e.target.value)}
              >
                <MenuItem value="EXTERNAL">Cliente externo</MenuItem>
                <MenuItem value="FRANCHISE">Franquicia</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {client.clientType === 'FRANCHISE' && (
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required error={!!errors.defaultDestinationLocationId}>
                <InputLabel>Franquicia asignada</InputLabel>
                <Select
                  value={client.defaultDestinationLocationId}
                  label="Franquicia asignada"
                  onChange={e => handleFieldChange('defaultDestinationLocationId', e.target.value)}
                >
                  {locations.map(location => (
                    <MenuItem key={location.id} value={location.id}>
                      {location.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.defaultDestinationLocationId && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                    {errors.defaultDestinationLocationId}
                  </Typography>
                )}
              </FormControl>
            </Grid>
          )}

          {/* Acuerdo Comercial */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mt: 2 }}>
              {t('commercial_agreement')}
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <TextField
              label={t('credit_days')}
              fullWidth
              type="number"
              value={client.creditDays || ''}
              onChange={e => handleFieldChange('creditDays', parseInt(e.target.value) || '')}
              placeholder="30"
              InputProps={{ endAdornment: t('days') }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label={t('credit_limit')}
              fullWidth
              type="number"
              value={client.creditLimit || ''}
              onChange={e => handleFieldChange('creditLimit', parseFloat(e.target.value) || '')}
              placeholder="10000"
              InputProps={{ startAdornment: '$' }}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Perfil comercial</InputLabel>
              <Select
                value={client.commercialProfileId}
                label="Perfil comercial"
                onChange={e => handleFieldChange('commercialProfileId', e.target.value)}
              >
                <MenuItem value="">Sin perfil</MenuItem>
                {commercialProfiles.map(profile => (
                  <MenuItem key={profile.id} value={profile.id}>
                    {profile.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Notas */}
          <Grid item xs={12}>
            <TextField
              label={t('notes')}
              fullWidth
              multiline
              rows={3}
              value={client.notes}
              onChange={e => handleFieldChange('notes', e.target.value)}
              placeholder={t('notes_placeholder')}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t('cancel')}</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={profilesLoading || profilePrerequisiteMissing}
        >
          {t('save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddB2BClientModal;
